/**
 * При отправке в цех: IGES и оформленный чертёж для крепежей с новым названием.
 */
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Stage, Layer } from 'react-konva';
import { workshopFastenerJobs } from '../components/fastenersData';
import { SheetPage, SHEET_W, SHEET_H } from '../components/FastenerSheetWindow';
import { projectFastenerDrawing } from './igesExporter';
import { loadSheetBackgroundImage } from './sheetBackground';
import { persistBlob } from './fileSystemSave';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const YK02_UGL_FILE = 'YK-02UGL(40х20, L=113.14, AISI201(1.5мм), шт.).IGS';

function safeFilePart(name) {
  return String(name || 'Крепеж').replace(/[<>:"/\\|?*]/g, '_').trim() || 'Крепеж';
}

function CaptureSheet({ job, payload, background, workshopInfo, productTitle, onReady }) {
  const stageRef = useRef(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !stageRef.current) return;
    fired.current = true;
    onReady(stageRef.current);
  });

  return (
    <Stage ref={stageRef} width={SHEET_W} height={SHEET_H}>
      <Layer>
        <SheetPage
          partKey={job.partKey}
          size={job}
          payload={payload}
          error=""
          background={background}
          workshopInfo={workshopInfo}
          productTitle={productTitle}
        />
      </Layer>
    </Stage>
  );
}

async function stageToPdfBlob(stage, fileName) {
  try {
    await document.fonts.ready;
  } catch (_) {
    // Шрифт подставится запасной, чертёж всё равно снимаем.
  }

  const stageWidth = stage.width();
  const pixelRatio = Math.max(1.5, 1754 / stageWidth);
  const refRatio = 1.5;
  if (pixelRatio !== 1) {
    stage.find('Line, Rect, Circle, Arrow, Path, Arc').forEach((node) => {
      const sw = node.strokeWidth();
      if (sw <= 0) return;
      node.setAttr('_origSW', sw);
      const divisor = node.strokeScaleEnabled() === false ? pixelRatio : refRatio;
      node.strokeWidth(sw / divisor);
    });
    stage.batchDraw();
  }

  const dataUrl = stage.toDataURL({ pixelRatio });
  stage.find('Line, Rect, Circle, Arrow, Path, Arc').forEach((node) => {
    const orig = node.getAttr('_origSW');
    if (orig !== undefined) {
      node.strokeWidth(orig);
      node.setAttr('_origSW', undefined);
    }
  });

  const outW = Math.round(stage.width() * pixelRatio);
  const outH = Math.round(stage.height() * pixelRatio);
  const page = document.createElement('canvas');
  page.width = outW;
  page.height = outH;
  const ctx = page.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      resolve();
    };
    img.onerror = () => reject(new Error('Не удалось снять чертёж крепежа'));
    img.src = dataUrl;
  });
  const jpegDataUrl = page.toDataURL('image/jpeg', 0.95);

  const response = await fetch(`${BACKEND_URL}/generate-drawing-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      png_base64: jpegDataUrl.split(',')[1],
      filename: `${fileName}.pdf`,
      landscape: false,
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `Ошибка PDF: ${response.status}`);
  }
  return response.blob();
}

function renderSheetPdf(job, payload, background, workshopInfo, productTitle) {
  return new Promise((resolve, reject) => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-12000px;top:0;pointer-events:none;';
    document.body.appendChild(host);
    const root = createRoot(host);
    const finish = () => {
      root.unmount();
      host.remove();
    };
    root.render(
      <CaptureSheet
        job={job}
        payload={payload}
        background={background}
        workshopInfo={workshopInfo}
        productTitle={productTitle}
        onReady={async (stage) => {
          try {
            await new Promise((done) => {
              requestAnimationFrame(() => requestAnimationFrame(done));
            });
            const blob = await stageToPdfBlob(stage, `Для цеха ${safeFilePart(job.name)}`);
            finish();
            resolve(blob);
          } catch (err) {
            finish();
            reject(err);
          }
        }}
      />,
    );
  });
}

async function saveFastenerIges(job, directoryHandle, quantity) {
  const response = await fetch(`${BACKEND_URL}/generate-fastener-iges`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      partKey: job.partKey,
      axialDistance: job.axialDistance,
      overhang: job.overhang,
      rangeOffset: job.rangeOffset,
      quantity,
      name: job.name,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `Ошибка IGES: ${response.status}`);
  }
  const files = Array.isArray(payload.files) ? payload.files : [];
  if (files.length === 0) {
    throw new Error('Сервер не вернул профили крепежа');
  }
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    const binary = atob(file.iges || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/iges' });
    await persistBlob(blob, file.filename || `${job.name}.iges`, { directoryHandle });
  }
}

/**
 * @returns {Promise<string[]>} названия, для которых файл не собрался
 */
async function saveYk02Ugl(quantity, directoryHandle) {
  const url = `${process.env.PUBLIC_URL || ''}/fastener/${encodeURIComponent(YK02_UGL_FILE)}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Не найден профиль YK-02UGL');
  }
  const blob = await response.blob();
  const filename = YK02_UGL_FILE.replace(', шт.', `, ${quantity}шт.`).replace(/\.IGS$/i, '.iges');
  await persistBlob(blob, filename, { directoryHandle });
}

export async function exportWorkshopFastenerFiles(fittings, directoryHandle = null, workshopInfo = null, productTitle = '') {
  const jobs = workshopFastenerJobs(fittings);
  if (jobs.length === 0) return [];

  const orderQty = Math.max(1, parseInt(workshopInfo?.quantity, 10) || 1);
  const needsSheet = jobs.some((job) => job.needsDrawing);
  const background = needsSheet ? await loadSheetBackgroundImage(false, false) : null;
  const failed = [];
  for (let index = 0; index < jobs.length; index += 1) {
    const job = jobs[index];
    try {
      const fasteners = Math.max(1, job.count || 1) * orderQty;
      if (job.needsProfiles) {
        await saveFastenerIges(job, directoryHandle, fasteners);
      }
      if (job.needsDrawing) {
        const payload = await projectFastenerDrawing(job);
        const pdf = await renderSheetPdf(job, payload, background, workshopInfo, productTitle);
        await persistBlob(pdf, `Для цеха ${safeFilePart(job.name)}.pdf`, { directoryHandle });
      }
    } catch (err) {
      console.error(`Файлы крепежа ${job.name}:`, err);
      failed.push(`${job.name}: ${err?.message || 'не удалось сохранить'}`);
    }
  }

  const uglPieces = jobs.reduce((sum, job) => {
    const pieces = Math.max(1, job.count || 1) * orderQty;
    if (job.partKey === 'YKRS-02') return sum + pieces * 2;
    if (job.partKey === 'YKWK-02') return sum + pieces;
    return sum;
  }, 0);
  if (uglPieces > 0) {
    try {
      await saveYk02Ugl(uglPieces, directoryHandle);
    } catch (err) {
      console.error('Профиль YK-02UGL:', err);
      failed.push(`YK-02UGL: ${err?.message || 'не удалось сохранить'}`);
    }
  }
  return failed;
}
